import { useCallback, useMemo, useState } from 'react';
import { Autocomplete, Badge, Box, Button, Chip, IconButton, Menu, TextField, Tooltip } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import EntityFilterSelect from './EntityFilterSelect';
import { EntityOption, ENTITY_FILTERS, ENTITY_FILTER_ORDER } from './entity-fetchers';

export interface FilterEntry { field: string; value: string; label: string }


interface Props {
  filters: FilterEntry[];
  onFilters: (next: FilterEntry[] | ((prev: FilterEntry[]) => FilterEntry[])) => void;
  /** Which entity the box edits. */
  field: string;
  onField: (field: string) => void;
  fields?: string[];
  /** Label a field for the hidden-filter list; the report renders v1..v10 by their real names. */
  labelForField?: (field: string) => string;
  /** Stretch to the container width (the value grows to fill) — e.g. inside the filters popover. */
  fullWidth?: boolean;
}

/** Filters on other fields sit behind a badge rather than as chips, so their count never widens the toolbar. */
export default function EntityFilterPicker({
  filters, onFilters, field, onField, fields = ENTITY_FILTER_ORDER, labelForField, fullWidth,
}: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const value = useMemo(
    () => filters.filter((f) => f.field === field).map((f) => ({ id: f.value, name: f.label })),
    [filters, field],
  );
  const hidden = useMemo(() => filters.filter((f) => f.field !== field), [filters, field]);

  const setValue = useCallback((items: EntityOption[]) => {
    onFilters((prev) => [
      ...prev.filter((f) => f.field !== field),
      ...items.map((it) => ({ field, value: it.id, label: it.name })),
    ]);
  }, [field, onFilters]);

  const label = labelForField ?? ((f: string) => ENTITY_FILTERS[f] ?? f);

  return (
    <>
      <Box sx={{ display: 'flex', minWidth: 0, width: fullWidth ? '100%' : { xs: '100%', md: 'auto' } }}>
        <Autocomplete
          size="small"
          disableClearable
          options={fields}
          value={field}
          onChange={(_, next) => onField(next)}
          getOptionLabel={(f) => ENTITY_FILTERS[f] ?? f}
          sx={{
            width: { xs: 120, md: 190 },
            flexShrink: 0,
            '& fieldset': { borderRight: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{ ...params.inputProps, 'aria-label': 'Filter field' }}
              InputProps={{
                ...params.InputProps,
                startAdornment: hidden.length > 0 && (
                  <Tooltip title={hidden.map((f) => `${label(f.field)}: ${f.label}`).join(' · ')}>
                    {/* Negative margin keeps the button from stretching the input's height. */}
                    <IconButton size="small" sx={{ my: -0.5 }} onClick={(e) => setAnchor(e.currentTarget)}>
                      <Badge badgeContent={hidden.length} color="primary">
                        <FilterList fontSize="small" />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          )}
        />
        {/* No label: the picker beside it already names the entity, and repeating
            it read as two identical controls. Leaves the "All" placeholder visible. */}
        <EntityFilterSelect
          field={field}
          value={value}
          onChange={setValue}
          label=""
          sx={{
            minWidth: { xs: 0, md: 220 },
            flex: fullWidth ? '1 1 auto' : { xs: '1 1 0', md: '0 1 auto' },
            maxWidth: fullWidth ? 'none' : 340,
            '& fieldset': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
          }}
        />
      </Box>

      <Menu open={Boolean(anchor) && hidden.length > 0} anchorEl={anchor} onClose={() => setAnchor(null)}>
        <Box sx={{ px: 1.5, py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: 360 }}>
          {hidden.map((f) => (
            <Chip
              key={`${f.field}:${f.value}`}
              size="small"
              color="primary"
              variant="outlined"
              label={`${label(f.field)}: ${f.label}`}
              onDelete={() => onFilters((prev) => prev.filter((x) => !(x.field === f.field && x.value === f.value)))}
            />
          ))}
          <Button
            size="small"
            onClick={() => {
              onFilters((prev) => prev.filter((f) => f.field === field));
              setAnchor(null);
            }}
          >
            Clear all
          </Button>
        </Box>
      </Menu>
    </>
  );
}
